import {
	ChevronDown,
	ChevronUp,
	Filter,
	Input,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@components/atoms';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuCheckboxItem,
} from '@components/molecules';
import type { DataGridColumnDef, DataGridFilterOptionsState, DataGridStrings } from '@components/organisms';
import * as React from 'react';
import {
	DEFAULT_FILTER_GROUP_NO_DATA_MESSAGE,
	DEFAULT_FILTER_GROUP_SEARCH_PLACEHOLDER,
} from '../../data-grid.constants';
import type { FilterOptionsState, DataGridFilterOption } from './hooks/use-filters';

/**
 * Configuration for handling filter option changes
 * @interface FilterOptionChange
 * @property {string} groupKey - Identifier for the filter group being changed
 * @property {boolean} isSelected - Whether the option is being selected or deselected
 * @property {string} label - Display label for the filter option
 * @property {string} optionKey - Unique identifier for the filter option
 */
export interface FilterOptionChange {
	groupKey: string;
	isSelected: boolean;
	label: string;
	optionKey: string;
}

/**
 * Current state of selected filters
 * @interface FilterState
 * @property {Map<string, string>} labels - Mapping of filter values to their display labels
 * @property {string} [paramName] - Optional parameter name for the filter in API calls
 * @property {Set<any>} values - Set of currently selected filter values
 */
export interface FilterState {
	labels: Map<string, string>;
	paramName?: string;
	values: Set<any>;
}

/**
 * Props for an individual filter group component
 * @interface FilterGroupProps
 * @template T - Type of the data items being filtered
 * @template CD - Type of the column definitions
 *
 * @property {any[]} [apiArgs] - Optional arguments passed to API calls for filter options
 * @property {DataGridColumnDef<CD>} column - Column definition this filter group is based on
 * @property {T[]} data - Array of data items to derive filter options from
 * @property {FilterOptionsState} filterState - Current state of the filter options
 * @property {boolean} isActive - Whether this filter group is currently expanded
 * @property {(params: FilterOptionChange | FilterOptionChange[]) => void} onFilterChange - Handler for filter selection changes
 * @property {() => void} [onRefreshClick] - Optional handler for refreshing filter options
 * @property {FilterState | undefined} selectedFilter - Currently selected filters for this group
 * @property {boolean} [showFilterGroupSearch] - Whether to show a search input for filter options
 * @property {boolean} [showFilterGroupSelectAll] - Whether to show a "Select All" option for filter options
 * @property {DataGridStrings} [strings] - Optional localization strings
 */
export interface FilterGroupProps<T> {
	apiArgs?: any[];
	column: DataGridColumnDef<T>;
	data?: T[];
	filterState: FilterOptionsState;
	isActive: boolean;
	onFilterChange: (params: FilterOptionChange | FilterOptionChange[]) => void;
	onRefreshClick?: () => void;
	selectedFilter: FilterState | undefined;
	showFilterGroupSearch?: boolean;
	showFilterGroupSelectAll?: boolean;
	strings?: DataGridStrings;
}

const DEFAULT_FILTER_GROUP_OPTIONS_SHOW_SEARCH_LIMIT = 8;

/**
 * Props for the main FilterMenu component
 * @interface FilterMenuProps
 * @template T - Type of the data items being filtered
 * @template CD - Type of the column definitions
 *
 * @property {any[]} [apiArgs] - Optional arguments passed to API calls for filter options
 * @property {DataGridColumnDef<CD>[]} columns - Array of column definitions
 * @property {T[]} data - Array of data items to filter
 * @property {(params: FilterOptionChange | FilterOptionChange[]) => void} onFilterChange - Handler for filter selection changes
 * @property {Record<string, FilterState>} selectedFilters - Currently selected filters for all groups
 * @property {boolean} [showFilterGroupSearch] - Whether to show a search input for filter groups
 * @property {boolean} [showFilterGroupSelectAll] - Whether to show a "Select All" option for filter groups
 * @property {DataGridStrings} [strings] - Optional localization strings
 */
export interface FilterMenuProps<T> {
	apiArgs?: any[];
	onFilterChange: (params: FilterOptionChange | FilterOptionChange[]) => void;
	selectedFilters: Record<string, FilterState>;
	showFilterGroupSearch?: boolean;
	showFilterGroupSelectAll?: boolean;
	strings?: DataGridStrings;
	filterableColumns: DataGridColumnDef<T>[];
	filterStates?: DataGridFilterOptionsState;
}

const FILTER_GROUP_BLUR_TIMEOUT = 200;

const FilterGroup = React.memo(
	function FilterGroup<T>({
		column,
		filterState: { options = [], isLoading, isInitialized },
		isActive,
		onFilterChange,
		selectedFilter,
		showFilterGroupSearch,
		showFilterGroupSelectAll,
		strings,
	}: FilterGroupProps<T>) {
		const blurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
		const [optimisticSelections, setOptimisticSelections] = React.useState(new Map());
		const [searchQuery, setSearchQuery] = React.useState('');
		const searchInputRef = React.useRef<HTMLInputElement>(null);
		const [isSearchFocused, setIsSearchFocused] = React.useState(false);

		const shouldShowSearch =
			showFilterGroupSearch === true ||
			(options.length > DEFAULT_FILTER_GROUP_OPTIONS_SHOW_SEARCH_LIMIT && showFilterGroupSearch !== false);

		const filteredOptions = React.useMemo(() => {
			if (!searchQuery.length) return options;

			const query = searchQuery.toLowerCase();

			return options.filter(option => String(option.label).toLowerCase().includes(query));
		}, [options, searchQuery]);

		const isAllSelected = React.useMemo(() => {
			return selectedFilter && options.length > 0
				? options.every(option => selectedFilter.values.has(String(option.value)))
				: false;
		}, [options, selectedFilter]);

		const handleSelectAllChange = React.useCallback(
			(checked: boolean) => {
				const changes: { isSelected: boolean; label: string; value: string }[] = [];

				setOptimisticSelections(prev => {
					const next = new Map(prev);

					options.forEach(option => {
						const value = String(option.value);
						const isChecked = selectedFilter?.values.has(value) || false;

						if (checked && !isChecked) {
							changes.push({ isSelected: true, label: option.label, value });
							next.set(value, true);
						} else if (!checked && isChecked) {
							changes.push({ isSelected: false, label: option.label, value });
							next.set(value, false);
						}
					});

					return next;
				});

				if (changes.length > 0) {
					const batchedChanges = changes.map(change => ({
						groupKey: column.key,
						isSelected: change.isSelected,
						label: change.label,
						optionKey: change.value,
					}));

					onFilterChange(batchedChanges);
				}
			},
			[options, selectedFilter, column.key, onFilterChange]
		);

		const handleSelectSingleChange = React.useCallback(
			(option: DataGridFilterOption) => (checked: boolean) => {
				setOptimisticSelections(prev => {
					const next = new Map(prev);
					next.set(String(option.value), checked);
					return next;
				});

				onFilterChange({
					groupKey: column.key,
					isSelected: checked,
					label: option.label,
					optionKey: String(option.value),
				});
			},
			[onFilterChange, column.key]
		);

		const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			// Cancel any previous blur timeout
			if (blurTimeoutRef.current) {
				clearTimeout(blurTimeoutRef.current);
			}

			blurTimeoutRef.current = setTimeout(() => {
				// This prevents blur when clicking on filter options
				const filterGroupElement = e.target.closest('[data-id="data-grid-filter-group-container"]');

				if (!filterGroupElement?.contains(e.relatedTarget as Node)) {
					// Actual blur logic here if needed in future
				}
			}, FILTER_GROUP_BLUR_TIMEOUT);
		};

		// Cleanup search blur when unmounting
		React.useEffect(() => {
			return () => {
				if (blurTimeoutRef.current) {
					clearTimeout(blurTimeoutRef.current);
				}
			};
		}, []);

		const handleSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchQuery(e.target.value);
		}, []);

		const handleSearchFocus = () => {
			setIsSearchFocused(true);
		};

		// After state changes, ensure focus is maintained (necessary for debugging, keyboard navigation)
		React.useEffect(() => {
			if (isSearchFocused && searchInputRef.current) {
				// Use a small timeout to ensure DOM updates have completed
				const timeoutId = setTimeout(() => {
					searchInputRef.current?.focus();
				}, 0);

				return () => clearTimeout(timeoutId);
			}
		}, [filteredOptions.length, isSearchFocused, searchQuery]);

		if (!isActive) return null;

		const noDataMessage = strings?.filters?.searchNoDataMessage || DEFAULT_FILTER_GROUP_NO_DATA_MESSAGE;
		const placeholder = strings?.filters?.searchPlaceholder || DEFAULT_FILTER_GROUP_SEARCH_PLACEHOLDER;

		if (isLoading || !isInitialized) {
			return <div className="py-2 text-center text-sm text-gray-500">Loading...</div>;
		}

		return (
			<div data-aid="data-grid-filter-group-container" className="px-2 py-1">
				{shouldShowSearch && (
					<div className="my-2 px-1">
						<Input
							ref={searchInputRef}
							onBlur={handleSearchBlur}
							onChange={handleSearchChange}
							onFocus={handleSearchFocus}
							placeholder={placeholder}
							type="text"
							value={searchQuery}
							variant="search"
							width="100%"
						/>
					</div>
				)}
				{showFilterGroupSelectAll && (
					<>
						<DropdownMenuCheckboxItem
							key="select-all"
							checked={isAllSelected}
							onCheckedChange={handleSelectAllChange}
							onSelect={e => {
								e.preventDefault();
							}}
						>
							{strings?.filters?.selectAll || 'Select All'}
						</DropdownMenuCheckboxItem>

						<hr className="my-2 border-t" />
					</>
				)}

				{filteredOptions.length > 0 ? (
					filteredOptions.map(option => {
						const value = String(option.value);
						// Use optimistic state if available, fall back to props
						const isChecked = optimisticSelections.has(value)
							? optimisticSelections.get(value)
							: selectedFilter?.values.has(value) || false;

						return (
							<DropdownMenuCheckboxItem
								key={value}
								checked={isChecked}
								onCheckedChange={handleSelectSingleChange(option)}
								onSelect={e => {
									e.preventDefault();
								}}
							>
								{option.label}
							</DropdownMenuCheckboxItem>
						);
					})
				) : (
					<div className="py-2 text-center text-sm text-gray-500">{noDataMessage}</div>
				)}
			</div>
		);
	},
	(prevProps, nextProps) => {
		const prevOptions = prevProps.filterState.options;
		const nextOptions = nextProps.filterState.options;

		const optionsEqual =
			prevOptions.length === nextOptions.length &&
			prevOptions.every((option, i) => option.label === nextOptions[i].label && option.value === nextOptions[i].value);

		return (
			optionsEqual &&
			prevProps.isActive === nextProps.isActive &&
			prevProps.filterState.isLoading === nextProps.filterState.isLoading &&
			prevProps.filterState.isInitialized === nextProps.filterState.isInitialized &&
			setsAreEqual(prevProps.selectedFilter?.values, nextProps.selectedFilter?.values) &&
			prevProps.column === nextProps.column
		);
	}
) as <T>(props: FilterGroupProps<T>) => JSX.Element;

const DEFAULT_FILTER_STATE: FilterOptionsState = {
	hasError: false,
	isInitialized: false,
	isLoading: true,
	options: [],
};

/**
 * A flexible filter menu component for data grids that supports multiple filter groups with checkboxes
 *
 * @component
 * @template T - Type of the data items being filtered
 * @template CD - Type of the column definitions
 *
 * @example
 * // Basic usage with automatic filter options derived from data
 * <FilterMenu
 *   columns={[
 *     { key: 'status', header: 'Status', filter: true },
 *     { key: 'priority', header: 'Priority', filter: true }
 *   ]}
 *   data={items}
 *   onFilterChange={handleFilterChange}
 *   selectedFilters={{}}
 * />
 *
 * @example
 * // With custom filter options and localization
 * <FilterMenu
 *   columns={[
 *     {
 *       key: 'status',
 *       header: 'Status',
 *       filter: {
 *         options: [
 *           { label: 'Active', value: 'active' },
 *           { label: 'Inactive', value: 'inactive' }
 *         ]
 *       }
 *     }
 *   ]}
 *   data={items}
 *   onFilterChange={handleFilterChange}
 *   selectedFilters={currentFilters}
 *   strings={{
 *     filters: {
 *       buttonLabel: 'Filter Items',
 *       selectAll: 'Select All Options'
 *     }
 *   }}
 * />
 *
 * @example
 * // With API-based filter options
 * <FilterMenu
 *   apiArgs={['v2', { tenant: 'example' }]}
 *   columns={[
 *     {
 *       key: 'category',
 *       header: 'Category',
 *       filter: {
 *         async: true,
 *         fetchOptions: async (args) => {
 *           const [version, config] = args;
 *           return await fetchCategories(version, config);
 *         }
 *       }
 *     }
 *   ]}
 *   data={items}
 *   onFilterChange={handleFilterChange}
 *   selectedFilters={currentFilters}
 * />
 */
export function FilterMenu<T>({
	apiArgs,
	onFilterChange,
	selectedFilters,
	showFilterGroupSelectAll,
	strings,
	filterableColumns,
	filterStates,
}: FilterMenuProps<T>) {
	const [activeDropdown, setActiveDropdown] = React.useState<string | null>(null);

	if (filterableColumns.length === 0) return null;

	return (
		<div data-aid="data-grid-filter-menu">
			<DropdownMenu modal={false}>
				<TooltipProvider delayDuration={0} skipDelayDuration={0}>
					<Tooltip>
						<TooltipTrigger asChild>
							{
								<DropdownMenuTrigger
									aria-label={strings?.filters?.buttonLabel || 'Filter'}
									className="flex items-center justify-between rounded-md p-0 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden"
								>
									<Filter className="text-primary" />
								</DropdownMenuTrigger>
							}
						</TooltipTrigger>
						<TooltipContent>{strings?.filters?.buttonLabel || 'Filter'}</TooltipContent>
					</Tooltip>
				</TooltipProvider>

				<DropdownMenuContent
					scrollable
					className="min-w-[30rem] max-w-[30rem] p-0"
				>
					{filterableColumns.map(column => {
						const filterState = filterStates?.[column.key];
						const hasError = filterState?.hasError || false;
						const showFilterGroupSearch =
							typeof column.filter === 'object' && column.filter.search ? column.filter.search : undefined;

						return (
							<div
								data-aid="data-grid-filter-menu-container"
								key={column.key}
								className="border-b border-gray-200 last:border-b-0"
							>
								<button
									className={`flex w-full items-center justify-between px-4 py-2 text-sm font-medium text-primary ${
										activeDropdown === column.key ? 'bg-bgSecondary' : 'hover:bg-bgSecondary'
									}`}
									onClick={() => {
										if (hasError) {
											filterState?.retry?.();
										} else {
											if (!filterState?.isInitialized) {
												filterState?.retry?.();
											}
											setActiveDropdown(activeDropdown === column.key ? null : column.key);
										}
									}}
								>
									{column.header || column.key}
									<FilterGroupHeaderIcon isActive={activeDropdown === column.key} hasError={hasError} />
								</button>
								<FilterGroup<T>
									apiArgs={apiArgs}
									column={column}
									filterState={filterStates?.[column.key] || DEFAULT_FILTER_STATE}
									isActive={activeDropdown === column.key}
									onFilterChange={change => {
										if (Array.isArray(change)) {
											// Batch update: add the group key to each change.
											onFilterChange(
												change.map(c => ({
													groupKey: column.key,
													isSelected: c.isSelected,
													label: c.label,
													optionKey: String(c.optionKey),
												}))
											);
										} else {
											// Single update.
											onFilterChange({
												groupKey: column.key,
												isSelected: change.isSelected,
												label: change.label,
												optionKey: String(change.optionKey),
											});
										}
									}}
									selectedFilter={selectedFilters[column.key]}
									showFilterGroupSearch={showFilterGroupSearch}
									showFilterGroupSelectAll={showFilterGroupSelectAll}
									strings={strings}
								/>
							</div>
						);
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}

function extractFilterOptions<T, CD>(column: DataGridColumnDef<CD>, data: T[]): { label: string; value: any }[] {
	if (!column.filter) return [];

	// If explicit options are provided, use those
	if (typeof column.filter === 'object' && column.filter.options) {
		return column.filter.options;
	}

	// Otherwise, derive from data (useful for auto-populating what the current page is showing)
	const values = new Set(
		data
			.map(row => {
				const value = (row as any)[column.key];

				if (typeof column.filter === 'object' && column.filter.transform) {
					return column.filter.transform(value);
				}

				return value;
			})
			.filter(value => value != null)
	);

	return Array.from(values).map(value => ({
		label: String(value),
		value,
	}));
}

function FilterGroupHeaderIcon({
	hasError,
	isActive,
	retryText = 'Retry',
}: {
	hasError: boolean;
	isActive: boolean;
	retryText?: string;
}) {
	return (
		<span className="ml-2">
			{hasError ? <span className="text-red-500">{retryText}</span> : isActive ? <ChevronDown /> : <ChevronUp />}
		</span>
	);
}

function setsAreEqual<T>(a?: Set<T>, b?: Set<T>): boolean {
	if (a === b) return true;

	if (!a || !b || a.size !== b.size) return false;

	for (const item of a) {
		if (!b.has(item)) return false;
	}

	return true;
}
